import { Anchor, Box, Group, SimpleGrid, Stack, Text, Title, UnstyledButton } from "@mantine/core";
import { IconArrowLeft, IconArrowRight, IconBriefcase, IconBuilding, IconCoin, IconUserSearch, IconUsers } from "@tabler/icons-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { OnboardingShell } from "../../components/OnboardingShell";
import "../../components/authAnimations.css";
import { JOIN_ORG_HUB_COPY } from "../../lib/companyKyc";
import { JOIN_HUB_COPY } from "../../lib/kyc";
import { axioTokens } from "../../theme";

const PATH_CARDS = [
  {
    key: "fill",
    title: JOIN_HUB_COPY.fill.title,
    description: JOIN_HUB_COPY.fill.description,
    icon: IconUsers,
    accent: axioTokens.violet,
    iconBg: axioTokens.roleVioletGradient,
    testId: "join-fill",
  },
  {
    key: "post",
    to: JOIN_HUB_COPY.post.to,
    title: JOIN_HUB_COPY.post.title,
    description: JOIN_HUB_COPY.post.description,
    icon: IconBuilding,
    accent: axioTokens.roleBlue,
    iconBg: axioTokens.roleBlueGradient,
    testId: "join-company",
  },
  {
    key: "apply",
    to: JOIN_HUB_COPY.candidate.to,
    title: JOIN_HUB_COPY.candidate.title,
    description: JOIN_HUB_COPY.candidate.description,
    icon: IconUserSearch,
    accent: axioTokens.roleTeal,
    iconBg: axioTokens.roleTealGradient,
    testId: "join-candidate",
  },
] as const;

const FILL_ROLE_CARDS = [
  {
    ...JOIN_HUB_COPY.recruiter,
    title: JOIN_HUB_COPY.recruiter.hubTitle,
    icon: IconUsers,
    accent: axioTokens.violet,
    iconBg: axioTokens.roleVioletGradient,
    testId: "join-recruiter",
  },
  {
    ...JOIN_HUB_COPY.affiliate,
    title: JOIN_HUB_COPY.affiliate.hubTitle,
    icon: IconCoin,
    accent: axioTokens.roleTeal,
    iconBg: axioTokens.roleTealGradient,
    testId: "join-affiliate",
  },
  {
    ...JOIN_ORG_HUB_COPY.consultancy,
    title: JOIN_ORG_HUB_COPY.consultancy.hubTitle,
    icon: IconBriefcase,
    accent: axioTokens.roleBlue,
    iconBg: axioTokens.roleBlueGradient,
    testId: "join-consultancy",
  },
] as const;

function BulletList({ items, accent }: { items: readonly string[]; accent: string }) {
  return (
    <Stack gap="var(--axio-unit-8)">
      {items.map((item) => (
        <Group key={item} gap="var(--axio-unit-8)" align="flex-start" wrap="nowrap">
          <span className="featureDot" style={{ backgroundColor: accent, marginTop: "var(--axio-unit-6)" }} />
          <Text size="xs" c="dimmed" ta="left">
            {item}
          </Text>
        </Group>
      ))}
    </Stack>
  );
}

function ContinueHint({ accent }: { accent: string }) {
  return (
    <Group
      gap="var(--axio-unit-6)"
      mt="auto"
      pt="var(--axio-unit-20)"
      style={{ color: accent, fontWeight: "var(--axio-font-weight-bold)", fontSize: "var(--axio-font-size-body)" }}
    >
      {JOIN_HUB_COPY.continueLabel}
      <IconArrowRight size="var(--axio-unit-16)" />
    </Group>
  );
}

export function JoinHubPage() {
  const [step, setStep] = useState<"choose" | "fill">("choose");
  const isFill = step === "fill";

  return (
    <OnboardingShell
      role="hub"
      accent="violet"
      centerPanel
      heroTitle={
        <>
          {JOIN_HUB_COPY.heroTitleLead}
          <br />
          <span className="gradientText">AxioHire</span>
        </>
      }
      heroSubtitle={JOIN_HUB_COPY.heroSubtitle}
      highlights={[
        { icon: IconCoin, text: JOIN_HUB_COPY.highlights[0] },
        { icon: IconBriefcase, text: JOIN_HUB_COPY.highlights[1] },
        { icon: IconUsers, text: JOIN_HUB_COPY.highlights[2] },
      ]}
      footer={
        <>
          {JOIN_HUB_COPY.loginPrompt}{" "}
          <Anchor component={Link} to="/login" fw="var(--axio-font-weight-semibold)" c="violet.7">
            {JOIN_HUB_COPY.loginLabel}
          </Anchor>
        </>
      }
    >
      <Stack gap={axioTokens.unit[40]} ta="center">
        <div className="staggerEnter" style={{ animationDelay: "var(--axio-duration-stagger1)" }}>
          {isFill && (
            <Anchor
              component="button"
              type="button"
              onClick={() => setStep("choose")}
              fw="var(--axio-font-weight-semibold)"
              c="violet.7"
              mb="var(--axio-unit-16)"
              display="inline-flex"
            >
              <Group gap="var(--axio-unit-6)" justify="center">
                <IconArrowLeft size="var(--axio-unit-16)" />
                {JOIN_HUB_COPY.backLabel}
              </Group>
            </Anchor>
          )}
          <Title order={3} fw="var(--axio-font-weight-extra-bold)" style={{ fontFamily: axioTokens.fontHead }}>
            {isFill ? JOIN_HUB_COPY.fillStepHeading : JOIN_HUB_COPY.heading}
          </Title>
          <Text size="md" c="dimmed" mt="var(--axio-unit-8)" maw="var(--axio-unit-480)" mx="auto">
            {isFill ? JOIN_HUB_COPY.fillStepSubheading : JOIN_HUB_COPY.subheading}
          </Text>
        </div>

        <SimpleGrid type="container" cols={{ base: 1, [axioTokens.containerQuery.sm]: 2 }} spacing={axioTokens.unit[32]}>
          {isFill
            ? FILL_ROLE_CARDS.map((card, idx) => (
                <Box
                  key={card.to}
                  component={Link}
                  to={card.to}
                  className="roleCard staggerEnter"
                  data-testid={card.testId}
                  style={{ animationDelay: `calc(var(--axio-duration-quick) + ${idx} * var(--axio-duration-progress-delay))` }}
                >
                  <Box className="roleIconBox" style={{ background: card.iconBg }}>
                    <card.icon size="var(--axio-unit-28)" color={axioTokens.surface} stroke="var(--axio-stroke-width-subtle)" />
                  </Box>
                  <Text size="xs" fw="var(--axio-font-weight-bold)" c={card.accent} tt="uppercase" style={{ letterSpacing: "var(--axio-letter-spacing-caps)" }}>
                    {card.subtitle}
                  </Text>
                  <Title order={4} fw="var(--axio-font-weight-extra-bold)" mt="var(--axio-unit-6)" style={{ fontFamily: axioTokens.fontHead }}>
                    {card.title}
                  </Title>
                  <Stack gap={axioTokens.unit[32]} mt="var(--axio-unit-16)" ta="left" style={{ flex: "var(--axio-number-one)" }}>
                    <Stack gap="var(--axio-unit-8)">
                      <Text size="xs" fw="var(--axio-font-weight-bold)" c={card.accent} tt="uppercase" style={{ letterSpacing: "var(--axio-letter-spacing-caps)" }}>
                        {JOIN_HUB_COPY.youCanLabel}
                      </Text>
                      <BulletList items={card.youCan} accent={card.accent} />
                    </Stack>
                    <Stack gap="var(--axio-unit-8)">
                      <Text size="xs" fw="var(--axio-font-weight-bold)" c={card.accent} tt="uppercase" style={{ letterSpacing: "var(--axio-letter-spacing-caps)" }}>
                        {JOIN_HUB_COPY.requiredLabel}
                      </Text>
                      <BulletList items={card.required} accent={card.accent} />
                    </Stack>
                  </Stack>
                  <ContinueHint accent={card.accent} />
                </Box>
              ))
            : PATH_CARDS.map((card, idx) => {
                const delay = { animationDelay: `calc(var(--axio-duration-quick) + ${idx} * var(--axio-duration-progress-delay))` };
                const inner = (
                  <>
                    <Box className="roleIconBox" style={{ background: card.iconBg }}>
                      <card.icon size="var(--axio-unit-28)" color={axioTokens.surface} stroke="var(--axio-stroke-width-subtle)" />
                    </Box>
                    <Title order={4} fw="var(--axio-font-weight-extra-bold)" mt="var(--axio-unit-6)" style={{ fontFamily: axioTokens.fontHead }}>
                      {card.title}
                    </Title>
                    <Text size="sm" c="dimmed" mt="var(--axio-unit-10)" lh="var(--axio-line-height-airy)" style={{ flex: "var(--axio-number-one)" }}>
                      {card.description}
                    </Text>
                    <ContinueHint accent={card.accent} />
                  </>
                );
                if (card.key === "fill") {
                  return (
                    <UnstyledButton
                      key={card.key}
                      className="roleCard staggerEnter"
                      data-testid={card.testId}
                      style={{ ...delay, width: axioTokens.percentage.full }}
                      onClick={() => setStep("fill")}
                    >
                      {inner}
                    </UnstyledButton>
                  );
                }
                return (
                  <Box
                    key={card.key}
                    component={Link}
                    to={card.to}
                    className="roleCard staggerEnter"
                    data-testid={card.testId}
                    style={delay}
                  >
                    {inner}
                  </Box>
                );
              })}
        </SimpleGrid>
      </Stack>
    </OnboardingShell>
  );
}
